// Shared booking utilities.
// Business hours: 9:00, 17:00 America/New_York (US Eastern), Mon, Sun, 30-min slots.

export const BUSINESS_TZ = "America/New_York";
export const SLOT_MINUTES = 30;
export const BUSINESS_START_HOUR = 9; // 9 AM ET
export const BUSINESS_END_HOUR = 17; // 5 PM ET (exclusive)

/**
 * For a selected calendar day, return the list of slot start
 * Date objects (UTC instants) for 9:00, 9:30, ... 16:30 ET.
 */
export function getSlotsForDate(date: Date): Date[] {
  // Calendar dates are day labels, not instants to convert to Eastern time.
  const iso = isoCalendarDate(date);

  const slots: Date[] = [];
  for (let h = BUSINESS_START_HOUR; h < BUSINESS_END_HOUR; h++) {
    for (let m = 0; m < 60; m += SLOT_MINUTES) {
      const utc = etWallTimeToUtc(iso, h, m);
      slots.push(utc);
    }
  }
  return slots;
}

/** Preserve the browser-local year, month and day selected in a calendar. */
export function isoCalendarDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/**
 * Convert an Eastern-Time wall-clock (YYYY-MM-DD, hour, minute) to a UTC Date.
 * Handles DST by computing the offset of the target instant in ET.
 */
export function etWallTimeToUtc(isoDate: string, hour: number, minute: number): Date {
  // First guess: treat the wall time as if it were UTC, then correct by ET offset.
  const [y, mo, d] = isoDate.split("-").map(Number);
  const guess = new Date(Date.UTC(y, mo - 1, d, hour, minute, 0));
  const offsetMin = getTzOffsetMinutes(guess, BUSINESS_TZ);
  return new Date(guess.getTime() - offsetMin * 60_000);
}

/** Returns the offset in minutes for the given timezone at the given instant (e.g. -240 for EDT, -300 for EST). */
function getTzOffsetMinutes(date: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(date);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  const asUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour) === 24 ? 0 : Number(map.hour),
    Number(map.minute),
    Number(map.second),
  );
  return (asUTC - date.getTime()) / 60_000;
}

export function formatInBusinessTz(date: Date, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: BUSINESS_TZ, ...opts }).format(date);
}

export function formatInTz(date: Date, timeZone: string, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, ...opts }).format(date);
}

export function getLocalTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** YYYY-MM-DD string for a Date in the business TZ. */
export function isoDateInBusinessTz(date: Date): string {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: BUSINESS_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}
